"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Menu, X, Phone, Mail, ChevronDown, Facebook, Instagram, Youtube, Twitter, GraduationCap, UserCircle, Briefcase, Globe } from "lucide-react"
import { CareerFormModal } from "./career-form-modal"
import Image from "next/image"
// import { useState, useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
// import Image from 'next/image'
import { useLanguage } from "@/contexts/LanguageContext"
import { GoogleTranslator } from "./google-translator"

interface PopupData {
  id: string
  image_name: string
  hyperlink: string
  date_time: string
  is_active: string
}

interface ThoughtData {
  id: number
  thoughts: string
  hyperlink: string
  created_at: string
  is_active: number
}

const PopupFromAPI = ({ onClose }: { onClose: () => void }) => {
  const [popupData, setPopupData] = useState<PopupData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)

  // Test mode - set to true to bypass API and show test popup
  const TEST_MODE = false // Disabled to use real API data

  useEffect(() => {
    console.log('Starting popup fetch...')

    // Immediate fallback - if nothing happens in 3 seconds, stop loading
    const immediateTimeout = setTimeout(() => {
      console.warn('FORCE STOP: Popup loading taking too long')
      setIsLoading(false)
      setError('Loading stopped')
    }, 3000) // 3 seconds max

    const fetchPopup = async () => {
      try {
        console.log('Fetching popup data from API...')

        const response = await fetch('https://dextro-online.com/stjerome_school/API/get_popup.php?action=get_all')

        console.log('Response received:', response.status)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log('Raw API Response:', data)

        clearTimeout(immediateTimeout) // Clear timeout if we got data

        // Handle different response formats
        let popups = []
        if (Array.isArray(data)) {
          popups = data
        } else if (data?.data && Array.isArray(data.data)) {
          popups = data.data
        } else {
          console.warn('Unexpected API response structure:', data)
          setError('Invalid response format from server')
          return
        }

        console.log('Processed popups:', popups)

        if (popups.length === 0) {
          console.warn('No popups found in response')
          setError('No popups found')
          return
        }

        // Find first active popup
        const activePopup = popups.find((popup: any) => {
          const isActive = popup.is_active === '1' || popup.is_active === 1 || popup.status === 'active'
          console.log('Checking popup:', { popup, isActive })
          return isActive
        })

        if (!activePopup) {
          console.warn('No active popups found')
          setError('No active popups')
          return
        }

        console.log('Active popup found:', activePopup)
        console.log('Available fields:', Object.keys(activePopup))

        // Set popup data directly from API response
        setPopupData({
          id: activePopup.id,
          image_name: activePopup.image_name,
          hyperlink: activePopup.hyperlink,
          date_time: activePopup.date_time,
          is_active: activePopup.is_active
        })

        console.log('Popup data set successfully!')
      } catch (err) {
        // Prevent 500 errors from crashing the UI
        console.warn('Silent continuation: Error fetching popup:', err)
        clearTimeout(immediateTimeout) // Clear immediate timeout

        // Fail gracefully without crashing Next.js rendering
        setError('Failed to load popup safely')
        setPopupData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPopup()
  }, [])

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3" role="dialog" aria-modal="true">
        <div className="bg-white p-6 rounded-lg shadow-xl">
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (error || !popupData) {
    console.log('Not showing popup due to:', { error, hasPopupData: !!popupData })
    return null
  }

  // Construct image URL from image_name field
  const imageUrl = `https://dextro-online.com/stjerome_school/upload/${popupData.image_name}`

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-[720px] sm:max-w-[597px] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-white px-3 py-1 text-[#113545] shadow-md border border-[#113545]/20 hover:bg-gray-50"
          aria-label="Close"
        >
          × Close
        </button>

        <a
          href={popupData.hyperlink || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <div className="relative w-full h-[70vh] max-h-[80vh] overflow-hidden flex items-center justify-center bg-gray-50">
            {imageError ? (
              <div className="text-center p-6">
                <p className="text-gray-600 text-lg mb-2">Image not available</p>
                <p className="text-gray-400 text-sm">Click to visit link</p>
              </div>
            ) : (
              <img
                src={imageUrl}
                alt="School Popup"
                width={600}
                height={600}
                loading="lazy"
                className="max-w-full max-h-full object-contain"
                onError={() => {
                  console.error('Error loading image:', imageUrl)
                  setImageError(true)
                }}
              />
            )}
          </div>
        </a>
      </div>
    </div>
  )
}

// Custom translation system - no Google Translate needed

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [fontSize, setFontSize] = useState(100)
  const [isCareerModalOpen, setIsCareerModalOpen] = useState(false)
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false)
  const [showLandingPopup, setShowLandingPopup] = useState(false) // DISABLED - was true
  const [thoughts, setThoughts] = useState<ThoughtData[]>([])
  const pathname = usePathname()

  // Use language context
  const { language, setLanguage, t } = useLanguage()

  const languages = [
    { code: "en", name: "English", nativeName: "English" },
    { code: "hi", name: "Hindi", nativeName: "हिंदी" },
    { code: "bn", name: "Bengali", nativeName: "বাংলা" },
    { code: "te", name: "Telugu", nativeName: "తెలుగు" },
    { code: "mr", name: "Marathi", nativeName: "मराठी" },
    { code: "ta", name: "Tamil", nativeName: "தமிழ்" },
    { code: "gu", name: "Gujarati", nativeName: "ગુજરાતી" },
    { code: "kn", name: "Kannada", nativeName: "ಕನ್ನಡ" },
    { code: "ml", name: "Malayalam", nativeName: "മലയാളം" },
    { code: "pa", name: "Punjabi", nativeName: "ਪੰਜਾਬੀ" },
    { code: "or", name: "Odia", nativeName: "ଓଡ଼ିଆ" },
    { code: "as", name: "Assamese", nativeName: "অসমীয়া" },
    { code: "ne", name: "Nepali", nativeName: "नेपाली" },
    { code: "ur", name: "Urdu", nativeName: "اردو" },
  ]

  const toggleDropdown = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown)
  }

  const increaseFontSize = () => {
    if (fontSize < 150) {
      setFontSize(fontSize + 10)
      document.documentElement.style.fontSize = `${fontSize + 10}%`
    }
  }

  const decreaseFontSize = () => {
    if (fontSize > 70) {
      setFontSize(fontSize - 10)
      document.documentElement.style.fontSize = `${fontSize - 10}%`
    }
  }

  // Custom translation system handles all language switching


  const handleLanguageChange = (langCode: string) => {
    // Set cookie for Google Translate
    // The format is /auto/targetLang or /sourceLang/targetLang
    // We use /en/targetLang because source is English
    const domain = window.location.hostname
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=${domain}`
    document.cookie = `googtrans=/en/${langCode}; path=/;` // Fallback for localhost

    setLanguage(langCode)
    setActiveDropdown(null)
    setIsLanguageDropdownOpen(false)

    // Reload to apply translation
    // We used to use window.location.reload(), but that might be too abrupt
    // Let's try just setting the cookie and reloading
    window.location.reload()
  }

  // Fetch thought of the day safely
  useEffect(() => {
    const fetchThoughts = async () => {
      try {
        const response = await fetch('https://dextro-online.com/stjerome_school/API/get_tought.php?action=get_today')
        if (response.ok) {
          const data = await response.json()
          const activeThoughts = Array.isArray(data) ? data.filter((t: ThoughtData) => t.is_active === 1) : []
          setThoughts(activeThoughts)
        } else {
          console.warn(`Thought API returned non-OK status: ${response.status}. Faiing gracefully.`)
          setThoughts([])
        }
      } catch (error) {
        console.warn('Silent continuation: Expected fetching thoughts to sometimes fail (e.g. 500 error):', error)
        setThoughts([]) // Ensure state remains stable arrays
      }
    }
    fetchThoughts()
  }, [])

  // Show marketing popup only on landing page - DISABLED
  // useEffect(() => {
  //   if (pathname === "/") {
  //     const timer = setTimeout(() => setShowLandingPopup(true), 300)
  //     return () => clearTimeout(timer)
  //   }
  // }, [pathname])

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">

      {/* Desktop Top Bar */}
      <div className="bg-[#113545] text-white py-2 hidden lg:block">
        <div className="w-full  lg:px-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Contact Information */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" />
                <a href="tel:02502431706" className="text-xs hover:underline">02502431706</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" />
                <a href="https://mail.google.com/mail/?view=cm&fs=1&to=lokbhartigroup.contact@gmail.com" target="_blank" rel="noopener noreferrer" className="text-xs hover:underline">lokbhartigroup.contact@gmail.com</a>
              </div>
            </div>

            {/* Center: Three Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/*<a
                href="#alumni"
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 rounded-md transition-all text-xs font-semibold border-2 border-white shadow-md hover:shadow-lg no-underline text-[#113545]"
                aria-label="Alumni"
              >
                <GraduationCap className="h-4 w-4" />
                <span>Alumni</span>
              </a>
              <a
                href="https://stjerome.web.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 rounded-md transition-all text-xs font-semibold border-2 border-white shadow-md hover:shadow-lg no-underline text-[#113545]"
                aria-label="Student Panel"
              >
                <UserCircle className="h-4 w-4" />
                <span>Student Panel</span>
              </a>*/}
              {/* <button
                onClick={() => setIsCareerModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 rounded-md transition-all text-xs font-semibold border-2 border-white shadow-md hover:shadow-lg text-[#113545]"
                aria-label="Career"
              >
                <Briefcase className="h-4 w-4" />
                <span>Career</span>
              </button> */}
            </div>

            {/* Right: Social Icons, Zoom Buttons, and Google Translate */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Social Icons */}
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/lokbhartieducation#" target="_blank" rel="noopener noreferrer" className="hover:text-gray-200 transition-colors" aria-label="Facebook">
                  <Facebook className="h-4 w-4" />
                </a>
                <a href="#" className="hover:text-gray-200 transition-colors" aria-label="Instagram">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="#" className="hover:text-gray-200 transition-colors" aria-label="Youtube">
                  <Youtube className="h-4 w-4" />
                </a>
                <a href="#" className="hover:text-gray-200 transition-colors" aria-label="Twitter">
                  <Twitter className="h-4 w-4" />
                </a>
              </div>

              {/* Zoom Buttons */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={decreaseFontSize}
                  className="h-7 px-2 flex items-center justify-center rounded bg-white text-[#113545] hover:bg-gray-50 transition-all text-xs font-semibold"
                  aria-label="Decrease text size"
                >
                  A-
                </button>
                <button
                  onClick={increaseFontSize}
                  className="h-7 px-2 flex items-center justify-center rounded bg-white text-[#113545] hover:bg-gray-50 transition-all text-xs font-semibold"
                  aria-label="Increase text size"
                >
                  A+
                </button>
              </div>

              {/* Language Selector */}
              <div className="relative">
                <button
                  aria-label="Select Language"
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded text-[#113545] hover:bg-gray-50 transition-all text-xs font-medium"
                >
                  <Globe className="h-3.5 w-3.5" />
                  <span>{languages.find(l => l.code === language)?.nativeName || "English"}</span>
                  <ChevronDown className={`h-3 w-3 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLanguageDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[90]"
                      onClick={() => setIsLanguageDropdownOpen(false)}
                    />
                    <div
                      className="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-md py-2 w-44 z-[100] max-h-80 overflow-y-scroll"
                      onWheel={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchMove={(e) => e.stopPropagation()}
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${language === lang.code
                            ? 'bg-[#113545] text-white font-medium'
                            : 'text-gray-700 hover:bg-[#113545] hover:text-white'
                            }`}
                        >
                          {lang.nativeName}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>


      <div className="w-full px-2 lg:px-4">
        <div className="flex items-center justify-between py-2 lg:py-3 min-h-[60px] lg:min-h-[80px] gap-2 lg:gap-4">
          <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
            <div className="relative flex-shrink-0">
              <Image
                src="/images/logo.webp"
                alt="LOKBHARATI GROUP OF SCHOOLS & COLLEGES Logo"
                width={80}
                height={80}
                priority
                className="w-[50px] h-[50px] lg:w-[70px] lg:h-[70px] shadow-lg border-red-100"
              />
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              {/* <p className="text-[#113545] font-semibold text-[10px] lg:text-xs xl:text-sm leading-tight mb-0.5">
                LOKBHARATI SCHOOLS AND COLLEGES FOUNDATION
              </p> */}
              <h1 className="text-sm sm:text-base lg:text-lg xl:text-xl 2xl:text-2xl font-bold text-gray-800 tracking-tight leading-tight my-0.5 max-w-[200px] sm:max-w-none">
                LOKBHARATI GROUP OF<br /> SCHOOLS & COLLEGES
              </h1>
              {/* <p className="text-[#113545] font-medium text-[9px] lg:text-[10px] xl:text-xs leading-tight">
                Saphale (W), Palghar - 401 102.
              </p> */}
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-0.5 2xl:gap-1 flex-shrink-0">
            {/* Home */}
            <a
              href="/"
              className="px-1.5 py-1 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg"
            >
              Home
            </a>

            {/* About Us */}
            <div className="relative group">
              <button className="px-1.5 py-1 flex items-center gap-0.5 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg">
                About Us
                <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <a
                  href="/about-us"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  About Us
                </a>
                <a
                  href="/trustees"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Trustees
                </a>
                <a
                  href="/lokmanya-nallasopara"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Lokmanya High School & Jr. College Nallasopara
                </a>
                {/* <a
                  href="/bharti-academy-virar"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Bharti Academy English High School & Jr. College Virar
                </a> */}
                <a
                  href="/bharti-academy"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Bharti Academy High School & Jr. College Umergoan
                </a>
                <a
                  href="/zenith-manor"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Zenith English High School Manor-Palghar road
                </a>
                <a
                  href="/zenith-shahapur"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Zenith English High School Shahapur
                </a>
              </div>
            </div>

            {/* Academics */}
            <div className="relative group">
              <button className="px-1.5 py-1 flex items-center gap-0.5 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg">
                Academics
                <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <a
                  href="/academic-calender"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Academic Calender
                </a>
                <a
                  href="/academic-program"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Academic Program
                </a>
                <a
                  href="/co-curricular-activities"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Co-Curricular Activities
                </a>
                <a
                  href="/school-holidays"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  School Holidays
                </a>
                <a
                  href="/exam-time-table"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  Exam Time-Table
                </a>
                <a
                  href="/school-time-table"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  School Time-Table
                </a>
              </div>
            </div>

            {/* Infrastructure */}
            <a
              href="/infrastructure"
              className="px-1.5 py-1 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg"
            >
              Infrastructure
            </a>

            {/* Gallery */}
            <a
              href="/gallery"
              className="px-1.5 py-1 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg"
            >
              Gallery
            </a>

            {/* Parents Involvement */}
            <a
              href="/parent-involve"
              className="px-1.5 py-1 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg"
            >
              Parents Involvement
            </a>

            {/* Online Admission */}
            <div className="relative group">
              <button className="px-1.5 py-1 flex items-center gap-0.5 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg">
                Online Admission
                <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <a
                  href="/school-admission"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  School Admission
                </a>
                <a
                  href="/college-admission"
                  className="block px-4 py-2 text-gray-700 hover:bg-[#113545] hover:text-white transition-all"
                >
                  College Admission
                </a>
              </div>
            </div>

            {/* Career */}
            <a
              href="/career"
              className="px-1.5 py-1 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg"
            >
              Career
            </a>

            {/* Contact Us */}
            <a
              href="/contact"
              className="px-1.5 py-1 text-gray-700 hover:text-[#113545] transition-all font-medium whitespace-nowrap text-base 2xl:text-lg"
            >
              Contact Us
            </a>
          </nav>

          <Button variant="ghost" size="sm" className="xl:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {isMenuOpen && (
          <nav className="xl:hidden py-4 border-t bg-white">
            <div className="flex flex-col gap-1">
              {/* Quick Action Buttons */}
              <div className="px-4 mb-4">
                {/* <div className="grid grid-cols-3 gap-2 mb-3">
                  <a
                    href="#alumni"
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-[#113545] hover:bg-[#6b1515] rounded-lg transition-all shadow-md hover:shadow-lg no-underline text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <GraduationCap className="h-6 w-6" />
                    <span className="text-xs font-semibold">Alumni</span>
                  </a>
                  <a
                    href="https://stjerome.web.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-[#113545] hover:bg-[#6b1515] rounded-lg transition-all shadow-md hover:shadow-lg no-underline text-white"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <UserCircle className="h-6 w-6" />
                    <span className="text-xs font-semibold">Student Panel</span>
                  </a>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false)
                      setIsCareerModalOpen(true)
                    }}
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-[#113545] hover:bg-[#6b1515] rounded-lg transition-all shadow-md hover:shadow-lg text-white w-full"
                  >
                    <Briefcase className="h-6 w-6" />
                    <span className="text-xs font-semibold">Career</span>
                  </button>
                </div> */}

                {/* Language Selector */}
                <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                  <Globe className="h-4 w-4 text-[#113545] flex-shrink-0" />
                  <select
                    value={language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="flex-1 bg-white rounded border border-gray-300 text-sm font-medium text-gray-700 px-3 py-2 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#113545]"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25rem 1.25rem',
                      paddingRight: '2.5rem',
                    }}
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.nativeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Contact details */}
              <div className="bg-[#113545] text-white p-4 rounded-md mb-4 mx-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <a href="tel:+917770048644" className="text-sm hover:underline">+91 77700 48644</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a href="https://mail.google.com/mail/?view=cm&fs=1&to=lokbhartigroup.contact@gmail.com" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">lokbhartigroup.contact@gmail.com</a>
                  </div>
                </div>
              </div>

              {/* Home */}
              <a
                href="/"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>

              {/* About Us */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => toggleDropdown("about")}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                >
                  About Us
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeDropdown === "about" ? "rotate-180" : ""}`}
                  />
                </button>
                {activeDropdown === "about" && (
                  <div className="pl-4 pb-2 space-y-1">
                    <a
                      href="/about-us"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      About Us
                    </a>
                    <a
                      href="/trustees"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Trustees
                    </a>
                    <a
                      href="/lokmanya-nallasopara"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Lokmanya High School & Jr. College Nallasopara
                    </a>
                    {/* <a
                      href="/bharti-academy-virar"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Bharti Academy English High School & Jr. College Virar
                    </a> */}
                    <a
                      href="/bharti-academy"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Bharti Academy High School & Jr. College Umergoan
                    </a>
                    <a
                      href="/zenith-manor"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Zenith English High School Manor-Palghar road
                    </a>
                    <a
                      href="/zenith-shahapur"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Zenith English High School Shahapur
                    </a>
                  </div>
                )}
              </div>

              {/* Academics */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => toggleDropdown("academics")}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                >
                  Academics
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeDropdown === "academics" ? "rotate-180" : ""}`}
                  />
                </button>
                {activeDropdown === "academics" && (
                  <div className="pl-4 pb-2 space-y-1">
                    <a
                      href="/academic-calender"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Academic Calender
                    </a>
                    <a
                      href="/academic-program"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Academic Program
                    </a>
                    <a
                      href="/co-curricular-activities"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Co-Curricular Activities
                    </a>
                    <a
                      href="/school-holidays"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      School Holidays
                    </a>
                    <a
                      href="/exam-time-table"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Exam Time-Table
                    </a>
                    <a
                      href="/school-time-table"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      School Time-Table
                    </a>
                  </div>
                )}
              </div>

              {/* Infrastructure */}
              <a
                href="/infrastructure"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Infrastructure
              </a>

              {/* Gallery */}
              <a
                href="/gallery"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Gallery
              </a>

              {/* Parents Involvement */}
              <a
                href="/parent-involve"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Parents Involvement
              </a>

              {/* Online Admission */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => toggleDropdown("online-admission")}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                >
                  Online Admission
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeDropdown === "online-admission" ? "rotate-180" : ""}`}
                  />
                </button>
                {activeDropdown === "online-admission" && (
                  <div className="pl-4 pb-2 space-y-1">
                    <a
                      href="/school-admission"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      School Admission
                    </a>
                    <a
                      href="/college-admission"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      College Admission
                    </a>
                  </div>
                )}
              </div>

              {/* Career */}
              <a
                href="/career"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Career
              </a>

              {/* Contact Us */}
              <a
                href="/contact"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact Us
              </a>



              {/* Digital School */}
              {/* <div className="border-b border-gray-100">
                <button
                  onClick={() => toggleDropdown("digital")}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                >
                  Digital School
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activeDropdown === "digital" ? "rotate-180" : ""}`}
                  />
                </button>
                {activeDropdown === "digital" && (
                  <div className="pl-4 pb-2 space-y-1">
                    <a
                      href="#parents-portal"
                      className="block py-2 px-4 text-sm text-gray-600 hover:bg-[#113545] hover:text-white rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Parents Portal
                    </a>
                  </div>
                )}
              </div> */}

              {/* Career */}
              {/* <button 
                onClick={() => {
                  setIsMenuOpen(false)
                  setIsCareerModalOpen(true)
                }}
                className="w-full text-left py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
              >
                Career
              </button> */}

              {/* Student Panel */}
              {/* <a
                href="/Student Panel"
                className="py-3 px-4 text-gray-700 hover:bg-[#113545] hover:text-white rounded-md transition-all"
                onClick={() => setIsMenuOpen(false)}
              >
                Student Panel
              </a> */}
            </div>
          </nav>
        )}
      </div>

      {/* Career Form Modal */}
      <CareerFormModal isOpen={isCareerModalOpen} onClose={() => setIsCareerModalOpen(false)} />

      {/* Marquee Bar - below navbar */}
      <div className="bg-[#113545] text-white overflow-hidden select-none">
        <div className="relative whitespace-nowrap py-2 flex">
          {/* track duplicated for seamless loop */}
          <div className="inline-flex animate-[marquee_15s_linear_infinite] will-change-transform">
            <div className="mx-6 inline-flex items-center gap-4 text-white whitespace-nowrap">
              <MarqueeStar />
              <span className="font-semibold tracking-wide">WELCOME TO LOKBHARATI GROUP OF SCHOOLS & COLLEGES</span>
            </div>
            {/* Duplicate for seamless loop */}
            <div className="mx-6 inline-flex items-center gap-4 text-white whitespace-nowrap">
              <MarqueeStar />
              <span className="font-semibold tracking-wide">WELCOME TO LOKBHARATI GROUP OF SCHOOLS & COLLEGES</span>
            </div>
            <div className="mx-6 inline-flex items-center gap-4 text-white whitespace-nowrap">
              <MarqueeStar />
              <span className="font-semibold tracking-wide">WELCOME TO LOKBHARATI GROUP OF SCHOOLS & COLLEGES</span>
            </div>
            <div className="mx-6 inline-flex items-center gap-4 text-white whitespace-nowrap">
              <MarqueeStar />
              <span className="font-semibold tracking-wide">WELCOME TO LOKBHARATI GROUP OF SCHOOLS & COLLEGES</span>
            </div>
          </div>
        </div>
      </div>

      {/* marquee keyframes */}
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      {/* Popup from API - DISABLED */}
      {showLandingPopup && pathname === "/" && (
        <PopupFromAPI onClose={() => setShowLandingPopup(false)} />
      )}
      <GoogleTranslator />
    </header>
  )
}

function MarqueeStar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="flex-shrink-0">
      <path d="M12 2l2.2 5.4 5.8.5-4.4 3.7 1.4 5.6L12 15.9 7 17.2l1.4-5.6-4.4-3.7 5.8-.5L12 2z" fill="#ffffff" opacity="0.9" />
    </svg>
  )
}
